/**
 * Setup ▸ Library Files — what is in the libraries, and what may go.
 *
 * The original's `SF` opens a list of `.LIB` files on disk with Add and Delete
 * beside it, because that is what a library was: a file you put on the path.
 * SirCAD's are rows in a database — stock ones shared by everyone, and one of
 * your own that Build Component and the LCSC importer write into — so there is
 * no path to manage and nothing to add or remove at the file level.
 *
 * What there is instead, and what has had no screen at all: seeing what the
 * libraries actually hold. Every route into them until now was a search box —
 * type a name, get the parts matching it — which is no use for the two
 * questions people actually ask. Is the part I saved last week in there, and
 * under what name? And what did the LCSC importer call the thing it just
 * brought in? Neither can be answered by searching for something you cannot
 * remember.
 *
 * So: the libraries down one side, their contents down the other, a filter over
 * the top, and a way to delete what is yours. Stock parts are never deletable
 * and the screen says so rather than offering a button that fails.
 */

import type { PartInfo, PartSource, LibraryInfo } from '../parts.ts';
import { button, buttons, openDialog } from './dialog.ts';

export interface LibraryManagerOptions {
  parts: PartSource;
  /** True when somebody is signed in; deleting needs an account. */
  signedIn: boolean;
  /** Place this part on the drawing, so the screen is a way in as well as a report. */
  onPlace(part: PartInfo): void;
}

export function showLibraryManager(opts: LibraryManagerOptions): void {
  openDialog({
    title: 'Setup Library Files',
    // Two panes: the libraries beside their contents. At the narrow default
    // that is two columns of about a hundred and eighty pixels, which is not
    // enough for a part name beside its description.
    wide: true,
    build(body, handle) {
      const layout = document.createElement('div');
      layout.className = 'lib-layout';
      body.append(layout);

      const side = document.createElement('div');
      side.className = 'lib-side';
      const main = document.createElement('div');
      main.className = 'lib-main';
      layout.append(side, main);

      const filter = document.createElement('input');
      filter.type = 'search';
      filter.placeholder = 'Filter these parts…';
      filter.className = 'lib-filter';
      main.append(filter);

      const list = document.createElement('div');
      list.className = 'lib-parts';
      main.append(list);

      const msg = document.createElement('p');
      msg.className = 'modal-hint lib-message';
      body.append(msg);

      let libraries: LibraryInfo[] = [];
      let chosen: LibraryInfo | null = null;
      let parts: PartInfo[] = [];

      const say = (text: string): void => { msg.textContent = text; };

      /**
       * Draw the part list, filtered.
       *
       * Rebuilt from `parts` each time rather than hiding rows, because a
       * library can hold thousands and leaving them all in the tree makes the
       * filter feel broken on the big stock ones.
       */
      function drawParts(): void {
        const want = filter.value.trim().toLowerCase();
        const shown = want === ''
          ? parts
          : parts.filter((p) =>
            p.name.toLowerCase().includes(want)
            || p.description.toLowerCase().includes(want)
            || (p.footprint ?? '').toLowerCase().includes(want));

        list.replaceChildren();
        if (shown.length === 0) {
          const empty = document.createElement('p');
          empty.className = 'modal-hint';
          empty.textContent = parts.length === 0
            ? 'This library is empty.'
            : `Nothing here matches “${filter.value.trim()}”.`;
          list.append(empty);
          return;
        }

        for (const p of shown) {
          const row = document.createElement('div');
          row.className = 'lib-part';

          const name = document.createElement('button');
          name.type = 'button';
          name.className = 'lib-part-name';
          name.textContent = p.name;
          // A library screen you cannot place from is a screen you visit once.
          name.title = 'Place this part';
          name.addEventListener('click', () => {
            opts.onPlace(p);
            handle.close();
          });

          const about = document.createElement('span');
          about.className = 'lib-part-about';
          about.textContent = [
            p.description,
            p.footprint ? `pattern ${p.footprint}` : null,
            p.sections > 1 ? `${p.sections} gates` : null,
          ].filter(Boolean).join(' · ');

          row.append(name, about);

          // Deletable is the server's word, not a guess from the library id:
          // stock libraries are shared and are never writable whoever asks.
          if (p.deletable && opts.signedIn) {
            const del = document.createElement('button');
            del.type = 'button';
            del.className = 'part-del';
            del.textContent = '✕';
            del.title = `Remove ${p.name} from ${chosen?.title ?? 'this library'}`;
            del.addEventListener('click', () => {
              if (!window.confirm(
                `Remove ${p.name} from your library? Drawings already using it `
                + 'keep the copy they placed.')) return;
              void opts.parts.deletePart(p.id)
                .then(() => {
                  parts = parts.filter((q) => q.id !== p.id);
                  if (chosen) chosen.parts = Math.max(0, chosen.parts - 1);
                  drawLibraries();
                  drawParts();
                  say(`Removed ${p.name}.`);
                })
                .catch((e: Error) => say(`Could not remove ${p.name}: ${e.message}`));
            });
            row.append(del);
          }

          list.append(row);
        }
      }

      function drawLibraries(): void {
        side.replaceChildren();
        for (const lib of libraries) {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'lib-pick' + (lib.id === chosen?.id ? ' on' : '');
          const title = document.createElement('span');
          title.className = 'lib-pick-name';
          title.textContent = lib.title || lib.name;
          const count = document.createElement('span');
          count.className = 'lib-pick-count';
          count.textContent = `${lib.parts}`;
          b.append(title, count);
          b.title = `${lib.name} · ${lib.kind === 'sch' ? 'symbols' : 'footprints'}`;
          b.addEventListener('click', () => { void choose(lib); });
          side.append(b);
        }
      }

      async function choose(lib: LibraryInfo): Promise<void> {
        chosen = lib;
        drawLibraries();
        list.replaceChildren();
        say(`Reading ${lib.title || lib.name}…`);
        try {
          parts = await opts.parts.listParts(lib.id);
          say(`${lib.title || lib.name} — ${parts.length} `
            + `${lib.kind === 'sch' ? 'symbol' : 'footprint'}`
            + `${parts.length === 1 ? '' : 's'}`
            + (parts.some((p) => p.deletable)
              ? '. Yours to edit — the ✕ removes one.'
              : '. A stock library, shared and read-only.'));
          drawParts();
        } catch (e) {
          say(`Could not read ${lib.title || lib.name}: ${(e as Error).message}`);
        }
      }

      filter.addEventListener('input', () => drawParts());

      const row = buttons(body);
      button(row, 'Close', () => handle.close());

      say('Reading the libraries…');
      void opts.parts.libraries()
        .then((all) => {
          libraries = all;
          if (all.length === 0) {
            say('No libraries. The stock ones are installed with the server; '
              + 'your own is made the first time you save a part into it.');
            return;
          }
          drawLibraries();
          // Open on the first one rather than on an empty pane: the question is
          // almost always "what is in these", and a blank right-hand side
          // answers nothing.
          return choose(all[0]!);
        })
        .catch((e: Error) => say(`Could not read the libraries: ${e.message}`));
    },
  });
}
